"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoggerGlobal = void 0;
const tslib_1 = require("tslib");
const events_typed_1 = require("@video/events-typed");
const log_node_1 = require("@video/log-node");
const lodash_throttle_1 = tslib_1.__importDefault(require("lodash.throttle"));
const package_json_1 = tslib_1.__importDefault(require("./package-json"));
const byteLength_1 = tslib_1.__importDefault(require("./utils/byteLength"));
const supportedLevels_1 = require("./utils/supportedLevels");
const uuid_1 = require("./utils/uuid");
const globalScope = (_a = globalThis) !== null && _a !== void 0 ? _a : (function fn() {
    return this;
})();
const defaultWriterOptions = {
    maxRequestSize: 1024 * 1024,
    level: "warn",
    limit: 500,
    interval: 30000,
};
const fetchStub = () => {
    return Promise.resolve(new Response());
};
class LoggerGlobal extends events_typed_1.EventEmitter {
    constructor() {
        super();
        this.options = defaultWriterOptions;
        this.storage = [];
        this.packageContextStorage = [];
        this.globalContext = {
            meta: {
                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
                referrer: typeof document !== "undefined" ? document.referrer : "unknown",
                source: "client",
                logId: (0, uuid_1.uuidv4)(),
                loggerVer: package_json_1.default.version,
            },
        };
        this.watchTimeout = null;
        this.loggers = [];
        this.online = true;
        // Method
        this.throttleSend = (0, lodash_throttle_1.default)(this.send, 5000);
        if (globalScope.LivelyHttpWriterV4) {
            return globalScope.LivelyHttpWriterV4;
        }
        globalScope.LivelyHttpWriterV4 = this;
        if (globalScope.addEventListener != null) {
            globalScope.addEventListener("unload", () => this.send());
            globalScope.addEventListener("online", () => {
                this.online = true;
                this.send();
            });
            global.addEventListener("offline", () => {
                this.online = false;
            });
        }
    }
    static setOptions(options) {
        const gl = new LoggerGlobal();
        gl.setOptions(options);
    }
    setOptions(options) {
        if (options.host != null) {
            this.options.host = options.host;
        }
        if (options.level != null) {
            this.options.level = options.level;
        }
        if (options.maxRequestSize != null) {
            this.options.maxRequestSize = options.maxRequestSize;
        }
        if (options.limit != null) {
            this.options.limit = options.limit;
        }
        if (options.interval != null) {
            this.options.interval = options.interval;
        }
        this.resetTimeout();
    }
    static setGlobalAggregate(key, value) {
        const gl = new LoggerGlobal();
        gl.setGlobalAggregate(key, value);
    }
    setGlobalAggregate(key, value) {
        if (this.globalContext.aggregates == null) {
            this.globalContext.aggregates = { [key]: value };
        }
        else {
            this.globalContext.aggregates[key] = value;
        }
    }
    static removeGlobalAggregate(key) {
        const gl = new LoggerGlobal();
        gl.removeGlobalAggregate(key);
    }
    removeGlobalAggregate(key) {
        if (this.globalContext.aggregates != null) {
            if (this.globalContext.aggregates[key] != null) {
                delete this.globalContext.aggregates[key];
                if (Object.keys(this.globalContext.aggregates).length === 0) {
                    delete this.globalContext.aggregates;
                }
            }
        }
    }
    static clearGlobalAggregates() {
        const gl = new LoggerGlobal();
        gl.clearGlobalAggregates();
    }
    clearGlobalAggregates() {
        this.globalContext.aggregates = {};
    }
    static setGlobalMeta(key, value) {
        const gl = new LoggerGlobal();
        gl.setGlobalMeta(key, value);
    }
    setGlobalMeta(key, value) {
        if (this.globalContext.meta == null) {
            this.globalContext.meta = {
                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
                referrer: typeof document !== "undefined" ? document.referrer : "unknown",
                source: "client",
                logId: (0, uuid_1.uuidv4)(),
                loggerVer: package_json_1.default.version,
                [key]: value,
            };
        }
        else {
            this.globalContext.meta[key] = value;
        }
    }
    static removeGlobalMeta(key) {
        const gl = new LoggerGlobal();
        gl.removeGlobalMeta(key);
    }
    removeGlobalMeta(key) {
        if (this.globalContext.meta != null) {
            if (this.globalContext.meta[key] != null) {
                delete this.globalContext.meta[key];
            }
        }
    }
    resetTimeout() {
        if (this.options.interval) {
            if (this.watchTimeout != null) {
                if (typeof window !== "undefined") {
                    window.clearTimeout(this.watchTimeout);
                }
                else {
                    clearTimeout(this.watchTimeout);
                }
            }
            if (this.send != null) {
                if (typeof window !== "undefined") {
                    this.watchTimeout = window.setTimeout(this.send.bind(this), this.options.interval);
                }
                else {
                    this.watchTimeout = setTimeout(this.send.bind(this), this.options.interval);
                }
            }
        }
    }
    registerPackage(logger) {
        this.loggers.push(logger);
    }
    removePackage(logger) {
        const index = this.loggers.indexOf(logger);
        if (index > -1) {
            this.loggers.splice(index, 1);
        }
        this.loggers.push(logger);
    }
    add(log, logger) {
        if (!(0, supportedLevels_1.meetsLevel)(log.level, this.options.level)) {
            return;
        }
        this.storage.push(log);
        if (this.options.host == null || !this.online) {
            this.checkStorageLimit();
            return;
        }
        if (logger != null) {
            const index = this.packageContextStorage.indexOf(logger.packageContext);
            if (index === -1) {
                this.packageContextStorage.push(logger.packageContext);
            }
        }
        if ((0, supportedLevels_1.meetsLevel)(log.level, "error")) {
            this.throttleSend();
        }
    }
    send() {
        if (this.storage.length === 0) {
            this.resetTimeout();
            return;
        }
        if (this.options.host == null) {
            this.checkStorageLimit();
            this.resetTimeout();
            return;
        }
        const messages = this.storage.splice(0, this.options.limit);
        const aggregates = {};
        const meta = {};
        this.packageContextStorage.forEach((context) => {
            if (context.aggregates != null) {
                Object.keys(context.aggregates).forEach((k) => {
                    var _a;
                    const v = (_a = context.aggregates) === null || _a === void 0 ? void 0 : _a[k];
                    if (v != null) {
                        aggregates[k] = v;
                    }
                });
            }
            if (context.meta != null) {
                log_node_1.PackageMetaKeys.forEach((k) => {
                    var _a;
                    if (((_a = context.meta) === null || _a === void 0 ? void 0 : _a[k]) != null) {
                        meta[k] = context.meta[k];
                    }
                });
            }
        });
        const packageContext = {
            aggregates,
            meta,
        };
        this.packageContextStorage.splice(0, -1 * this.packageContextStorage.length);
        const body = {
            logs: messages,
            sendTime: new Date().toISOString(),
            context: {
                packageContexts: packageContext,
                globalContext: this.globalContext,
            },
        };
        let bodyJson;
        try {
            bodyJson = JSON.stringify(body);
        }
        catch (error) {
            this.add({
                level: "error",
                message: "Failed to stringify in logger",
                error: `${error}`,
                time: new Date().toISOString(),
            });
            this.resetTimeout();
            return;
        }
        if ((0, byteLength_1.default)(bodyJson) > this.options.maxRequestSize) {
            this.add({
                level: "error",
                message: `Body exceeds maxRequestSize. Dropped ${messages.length} logs.`,
                time: new Date().toISOString(),
            });
            this.resetTimeout();
            return;
        }
        LoggerGlobal.fetch(`${this.options.host}/client-logs/v4`, {
            method: "POST",
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
            },
            body: bodyJson,
        })
            .then((response) => {
            if (!response.ok) {
                throw new TypeError(response.status.toString());
            }
            this.resetTimeout();
            return response;
        })
            .catch((error) => {
            this.add({
                level: "network",
                message: "Network failure.",
                error: `${error}`,
                time: new Date().toISOString(),
            });
            this.resetTimeout();
        });
    }
    checkStorageLimit() {
        if (this.storage.length > this.options.limit) {
            this.storage.slice(-1 * this.options.limit);
        }
    }
}
exports.LoggerGlobal = LoggerGlobal;
LoggerGlobal.fetch = typeof window !== "undefined" ? window.fetch.bind(window) : fetchStub;
//# sourceMappingURL=logger-http-writer.js.map